import { useEffect } from 'react';

/**
 * The light in the glass follows the hour (the chat's own immersive detail): the white bloom sits high and
 * bright in the morning, drifts down while the brand blues deepen through the evening, and dims at night.
 * Only the bloom's position and the fields' strength move; the palette is the brand's.
 */

export interface HourLight {
   /** where the bloom sits, in % of the pane */
   x: number;
   y: number;
   /** the bloom's strength, 0..1 */
   bloom: number;
   /** a multiplier on the brand-blue fields */
   fields: number;
}

export function lightForHour(hour: number): HourLight {
   if (hour >= 5 && hour < 10) return { x: 18, y: 0, bloom: 1, fields: 0.8 }; // morning: pale, lit from the top
   if (hour >= 10 && hour < 17) return { x: 32, y: 0, bloom: 0.9, fields: 1 }; // day
   if (hour >= 17 && hour < 21) return { x: 10, y: 22, bloom: 0.75, fields: 1.15 }; // evening: the blues deepen
   return { x: 0, y: 42, bloom: 0.55, fields: 1.25 }; // night
}

export function applyHourLight(date: Date = new Date()): void {
   if (typeof document === 'undefined') return;
   const light = lightForHour(date.getHours());
   const style = document.documentElement.style;
   style.setProperty('--light-x', `${light.x}%`);
   style.setProperty('--light-y', `${light.y}%`);
   style.setProperty('--light-bloom', String(light.bloom));
   style.setProperty('--light-fields', String(light.fields));
}

/** Applies the hour's light now and again at the top of every hour. */
export function useHourLight(): void {
   useEffect(() => {
      applyHourLight();
      let timer: ReturnType<typeof setTimeout>;
      const schedule = () => {
         const now = new Date();
         const msToNextHour = (60 - now.getMinutes()) * 60_000 - now.getSeconds() * 1000 + 500;
         timer = setTimeout(() => {
            applyHourLight();
            schedule();
         }, msToNextHour);
      };
      schedule();
      return () => clearTimeout(timer);
   }, []);
}
